import { Container, Graphics, Sprite, Texture } from 'pixi.js';
import { RESOURCE_COLORS } from './AsteroidGraphics';
import { assetLoader } from '../core/AssetLoader';

// Particle texture cache
let particleTextureGenerated = false;
let particleTexture: Texture | null = null;

interface MiningSource {
  x: number;
  y: number;
  rotation: number;
}

interface Spark {
  sprite: Sprite;
  vx: number;
  vy: number;
  life: number;
  maxLife: number;
}

/**
 * Mining laser beam + sparks effect.
 * Draws a thin laser line from the ship to the asteroid surface,
 * plus spark particles at the impact point.
 */
export class MiningLaser extends Container {
  private source: MiningSource;
  private targetX: number;
  private targetY: number;
  private asteroidRadius: number;
  private resourceType: string;

  private sparks: Spark[] = [];
  private emitting: boolean = true;
  private spawnTimer: number = 0;

  // Laser beam graphics
  private beamGfx: Graphics;
  private beamGlow: Graphics;
  private beamPulseTimer: number = 0;

  // Parent container for particles (world container)
  private parentContainer: Container | null = null;

  constructor(
    source: MiningSource,
    targetX: number,
    targetY: number,
    asteroidRadius: number,
    resourceType: string
  ) {
    super();

    this.source = source;
    this.targetX = targetX;
    this.targetY = targetY;
    this.asteroidRadius = asteroidRadius;
    this.resourceType = resourceType;

    this.ensureParticleTextureExists();

    // Create beam graphics
    this.beamGlow = new Graphics();
    this.beamGlow.zIndex = 14;
    this.addChild(this.beamGlow);

    this.beamGfx = new Graphics();
    this.beamGfx.zIndex = 15;
    this.addChild(this.beamGfx);

    this.zIndex = 15;
  }

  private ensureParticleTextureExists(): void {
    if (particleTextureGenerated && particleTexture) return;

    const gfx = new Graphics();
    gfx.star(4, 4, 4, 4, 2);
    gfx.fill({ color: 0xffffff, alpha: 1 });
    particleTexture = assetLoader.generateTexture(gfx, 'mining-spark');
    gfx.destroy();
    particleTextureGenerated = true;
  }

  private getHitPoint(): { x: number; y: number } {
    const dx = this.targetX - this.source.x;
    const dy = this.targetY - this.source.y;
    const angle = Math.atan2(dy, dx);
    return {
      x: this.targetX - Math.cos(angle) * this.asteroidRadius,
      y: this.targetY - Math.sin(angle) * this.asteroidRadius,
    };
  }

  setParticleParent(parent: Container): void {
    this.parentContainer = parent;
  }

  /** Draw the laser beam from ship to asteroid surface */
  private drawBeam(): void {
    const hitPoint = this.getHitPoint();
    const resourceColor = RESOURCE_COLORS[this.resourceType] || 0x00ccff;

    // Pulsing alpha for the beam (subtle shimmer)
    const pulse = 0.7 + 0.3 * Math.sin(this.beamPulseTimer * 6);

    // Outer glow (wider, transparent)
    this.beamGlow.clear();
    this.beamGlow.setStrokeStyle({ width: 6, color: resourceColor, alpha: 0.15 * pulse });
    this.beamGlow.moveTo(this.source.x, this.source.y);
    this.beamGlow.lineTo(hitPoint.x, hitPoint.y);
    this.beamGlow.stroke();

    // Core beam (thin, bright)
    this.beamGfx.clear();
    this.beamGfx.setStrokeStyle({ width: 1.5, color: 0xffffff, alpha: 0.9 * pulse });
    this.beamGfx.moveTo(this.source.x, this.source.y);
    this.beamGfx.lineTo(hitPoint.x, hitPoint.y);
    this.beamGfx.stroke();

    // Inner color line
    this.beamGfx.setStrokeStyle({ width: 2.5, color: resourceColor, alpha: 0.6 * pulse });
    this.beamGfx.moveTo(this.source.x, this.source.y);
    this.beamGfx.lineTo(hitPoint.x, hitPoint.y);
    this.beamGfx.stroke();
  }

  private spawnSparks(count: number = 2): void {
    if (!this.parentContainer || !particleTexture || !this.emitting) return;

    const hitPoint = this.getHitPoint();
    const resourceColor = RESOURCE_COLORS[this.resourceType] || 0x888888;
    const colors = [resourceColor, resourceColor, 0xff6600, 0xffaa00, 0xffffff];

    // Sparks fly outward from impact point in a cone away from ship
    const baseAngle = Math.atan2(this.source.y - this.targetY, this.source.x - this.targetX);
    // Reverse — sparks scatter away from ship (into asteroid side)
    const scatterAngle = baseAngle + Math.PI;

    for (let i = 0; i < count; i++) {
      const sprite = new Sprite(particleTexture);
      sprite.anchor.set(0.5);
      sprite.position.set(hitPoint.x, hitPoint.y);
      sprite.tint = colors[Math.floor(Math.random() * colors.length)];
      sprite.scale.set(0.3 + Math.random() * 0.35);
      sprite.zIndex = 16;
      this.parentContainer.addChild(sprite);

      const spreadAngle = scatterAngle + (Math.random() - 0.5) * Math.PI * 1.2;
      const speed = 40 + Math.random() * 60;

      this.sparks.push({
        sprite,
        vx: Math.cos(spreadAngle) * speed,
        vy: Math.sin(spreadAngle) * speed,
        life: 250 + Math.random() * 250,
        maxLife: 250 + Math.random() * 250,
      });
    }
  }

  private updateSparks(delta: number): void {
    const dt = delta / 1000;

    for (let i = this.sparks.length - 1; i >= 0; i--) {
      const spark = this.sparks[i];
      spark.sprite.x += spark.vx * dt;
      spark.sprite.y += spark.vy * dt;
      spark.vx *= 0.97;
      spark.vy *= 0.97;
      spark.life -= delta;
      const lifeRatio = Math.max(0, spark.life / spark.maxLife);
      spark.sprite.alpha = lifeRatio;
      spark.sprite.scale.set(0.2 + 0.25 * lifeRatio);

      if (spark.life <= 0) {
        spark.sprite.destroy();
        this.sparks.splice(i, 1);
      }
    }
  }

  triggerHitBurst(): void {
    this.spawnSparks(8);
  }

  updateSource(source: MiningSource): void {
    this.source = source;
  }

  updateTarget(x: number, y: number): void {
    this.targetX = x;
    this.targetY = y;
  }

  stopEmitting(): void {
    this.emitting = false;
    this.beamGfx.clear();
    this.beamGlow.clear();
  }

  update(delta: number): void {
    this.beamPulseTimer += delta / 1000;

    // Draw beam every frame (positions update in real-time)
    if (this.emitting) {
      this.drawBeam();
    }

    // Spawn sparks periodically
    this.spawnTimer += delta;
    if (this.spawnTimer >= 60 && this.emitting) {
      this.spawnSparks(1 + Math.floor(Math.random() * 2));
      this.spawnTimer = 0;
    }

    this.updateSparks(delta);
  }

  destroy(): void {
    for (const spark of this.sparks) {
      spark.sprite.destroy();
    }
    this.sparks = [];
    this.beamGfx.destroy();
    this.beamGlow.destroy();
    super.destroy();
  }
}
